'use client';

import React from 'react';
import { ArrowDown } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { motion } from 'framer-motion';
import { usePathname } from 'next/navigation';
import { useScrollToSection } from '@/components/hooks/use-scroll-to-section';
import { Button } from '@/components/ui/button';
import { MotionLink } from '@/components/ui/Motion';
import SectionBase from '@/components/ui/sections/section-base';
import SectionHeader from '@/components/ui/sections/section-header';
import { TypographyH4, TypographySmall } from '@/components/ui/typography';
import { cls, getServicePath } from '@/lib/utils';
import { GETTING_STARTED_SECTION_ID } from '../GettingStarted/consts';
import INCLUDED_SECTION_CONTENT, {
  CTA_BUTTON_TEXT,
  INCLUDED,
  INCLUDED_SECTION_ID,
  SUB_TITLE,
  type IconName,
} from './consts';

const HREF = `#SECTION-${GETTING_STARTED_SECTION_ID}`;

const wrapperMotionProps = {
  initial: 'hidden' as const,
  whileInView: 'visible' as const,
  viewport: { once: true },
  variants: {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  },
};

const cardMotionProps = {
  variants: {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  },
};

interface IncludedItemProperties {
  title: string;
  content: string;
  name: IconName;
}

function IncludedItem({ title, content, name }: IncludedItemProperties) {
  return (
    <motion.div
      className={cls`flex flex-col gap-3 p-6 rounded-lg bg-background`}
      {...cardMotionProps}
    >
      <DynamicIcon name={name} size={32} className="text-primary" />
      <TypographyH4 className="m-0! text-foreground">{title}</TypographyH4>
      <TypographySmall className="m-0! text-muted-foreground leading-relaxed">
        {content}
      </TypographySmall>
    </motion.div>
  );
}

export default function Included() {
  const pathname = usePathname();
  const servicePath = getServicePath(pathname);
  const handleOnClick = useScrollToSection(HREF);

  if (!servicePath) {
    return null;
  }

  const { title, description } = INCLUDED_SECTION_CONTENT[servicePath];

  return (
    <SectionBase id={INCLUDED_SECTION_ID} background="bg-card">
      <motion.div
        className={cls`flex flex-col items-center gap-10`}
        {...wrapperMotionProps}
      >
        <SectionHeader
          subtitle={SUB_TITLE}
          title={title}
          description={description}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 w-full">
          {INCLUDED.map((item) => (
            <IncludedItem key={item.title} {...item} />
          ))}
        </div>

        <Button onClick={handleOnClick} asChild>
          <MotionLink href={HREF}>
            <ArrowDown className="mr-1" />
            {CTA_BUTTON_TEXT}
          </MotionLink>
        </Button>
      </motion.div>
    </SectionBase>
  );
}
